/**
 * Input handlers that do not move the caret.
 *
 * THE BUG THIS FIXES
 * Transforming a controlled input's value inside onChange — `toUpperCase()`,
 * stripping non-digits, and so on — makes React write a value back to the DOM
 * that differs from what the element currently holds. Assigning to
 * `input.value` resets the text cursor to the END of the field.
 *
 * Typing at the end of a field hides this completely, which is why it went
 * unnoticed. Editing the FIRST letter of an existing word makes it obvious:
 * the caret snaps to the end after every keystroke and the rest of the word is
 * typed backwards.
 *
 * THE FIX
 * `toUpperCase()` never changes a string's length, so the caret indices from
 * before the transform are still the correct ones afterwards. Capture them,
 * let React commit, then put the selection back.
 */

/**
 * Re-apply a selection range once React has written the new value to the DOM.
 *
 * requestAnimationFrame runs after the commit, which is the earliest point the
 * element actually holds the new value — restoring any sooner is overwritten.
 */
const restoreCaret = (input, start, end) => {
  if (start === null || start === undefined) return;
  requestAnimationFrame(() => {
    // Do nothing if focus moved on in the meantime — forcing a selection into
    // a field the user has left would steal the caret back.
    if (document.activeElement !== input) return;
    try {
      input.setSelectionRange(start, end);
    } catch (e) {
      // Some input types (number, email, date) do not support selection.
      // Nothing to restore there, and throwing would break the keystroke.
    }
  });
};

/**
 * Keep the caret in place for a hand-written transforming onChange.
 *
 * For handlers that do something the two helpers below do not cover (filtering
 * AND clamping, for instance). Call it with the raw and transformed values just
 * before setFieldValue.
 *
 * The caret is anchored on how many CHARACTERS SURVIVED the transform before
 * it, so it stays put whether or not the keystroke was filtered away.
 *
 * @param {HTMLInputElement} input - event.target
 * @param {string} rawValue        - input.value before the transform
 * @param {string} newValue        - the value being stored
 */
export const preserveCaret = (input, rawValue, newValue) => {
  const caret = input.selectionStart ?? 0;

  // Unchanged value means React will not rewrite the DOM, so the caret is safe.
  if (rawValue === newValue) return;

  // Count how much of the text before the caret survived, and place the caret
  // after that same amount in the new value.
  const before = rawValue.slice(0, caret);
  let kept = 0;
  let cursor = 0;
  for (const ch of before) {
    if (cursor < newValue.length && newValue[cursor] === ch) {
      cursor++;
      kept++;
    } 
  }
  restoreCaret(input, kept, kept);
};

/**
 * onChange handler that upper-cases the value without moving the caret.
 *
 * @param {Function} setFieldValue - Formik's setFieldValue
 * @param {string} fieldName
 * @param {Function} [onAfterChange] - optional extra work, given the new value
 * @returns {Function} an onChange handler
 *
 * @example
 *   <Field name="fullName" onChange={upperCaseChange(setFieldValue, "fullName")} />
 */
export const upperCaseChange =
  (setFieldValue, fieldName, onAfterChange) => (event) => {
    const input = event.target;
    const { selectionStart, selectionEnd } = input;

    const value = input.value.toUpperCase();
    setFieldValue(fieldName, value);
    restoreCaret(input, selectionStart, selectionEnd);

    if (onAfterChange) onAfterChange(value);
  };

/**
 * onChange handler that keeps digits only, without moving the caret.
 *
 * Unlike upper-casing, stripping characters DOES change the length, so the old
 * index would land in the wrong place. The caret is instead placed after the
 * same number of digits it previously followed, which is where the user
 * expects it whether or not their keystroke survived the filter.
 *
 * @param {Function} setFieldValue - Formik's setFieldValue
 * @param {string} fieldName
 * @param {Function} [onAfterChange] - optional extra work, given the new value
 */
export const digitsOnlyChange =
  (setFieldValue, fieldName, onAfterChange) => (event) => {
    const input = event.target;
    const { selectionStart } = input;
    const raw = input.value;

    const value = raw.replace(/\D/g, "");
    setFieldValue(fieldName, value);

    // How many digits sat before the caret? That count is stable across the
    // filter, so it is the anchor to restore against.
    const digitsBeforeCaret = raw
      .slice(0, selectionStart ?? 0)
      .replace(/\D/g, "").length;
    restoreCaret(input, digitsBeforeCaret, digitsBeforeCaret);

    if (onAfterChange) onAfterChange(value);
  };
